import request from "superagent";
import { authPlugin } from "./authPlugin";
import { errorHandlingPlugin } from "./errorHandlingPlugin";

/**
 * Error handling interceptor - chains unauthorized detection + error notifications
 *
 * Use this for requests that need error handling WITHOUT authentication.
 * For example: public APIs, health checks, or custom auth flows.
 *
 * errorHandlingPlugin execution order:
 * Handles custom error logic, 
 * Detects 401/403 and triggers redirect,
 * Handles errors and displays notifications
 *
 * IMPORTANT: Plugins handle error notifications and redirects, but promises
 * still reject. Calling code MUST use try-catch blocks to prevent uncaught
 * promise rejections, especially when accessing response properties like .body
 *
 * @example
 * // Public API without auth
 * request.get('/health')
 *   .use(createErrorInterceptor())
 *   .send();
 *
 * @example
 * // Custom auth + standard error handling
 * request.post('/api/data')
 *   .use(myCustomAuthPlugin())
 *   .use(createErrorInterceptor())
 *   .send();
 */
export function createBaseInterceptor() {
  return function (req: request.SuperAgentRequest) {
    return req.set("Accept", "application/json").use(errorHandlingPlugin());
  };
}

/**
 * Full API interceptor - combines authentication + error handling
 *
 * This is the default interceptor used by apiRequest in apiInterceptor.ts.
 * Uses createErrorInterceptor() internally to avoid code duplication.
 *
 * Plugin execution order:
 * 1. authPlugin - Adds authentication headers
 * 2. createErrorInterceptor() - Handles unauthorized + error notifications
 *
 * IMPORTANT: Plugins handle error notifications and redirects, but promises
 * still reject. Calling code MUST use try-catch blocks to prevent uncaught
 * promise rejections, especially when accessing response properties like .body
 *
 * @example
 * // Standard usage via apiRequest
 * const response = await apiRequest.get('/api/users').send();
 *
 * @example
 * // Direct usage
 * const response = await request.get('/api/users')
 *   .use(createApiInterceptor())
 *   .send();
 */
export function createAuthInterceptor() {
  return function (req: request.SuperAgentRequest) {
    return req
      .use(authPlugin())
      .use(createBaseInterceptor());
  };
}
